import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  MessageSquare,
  Trash2,
  Mail,
  Phone,
  User,
  ExternalLink,
  Loader2,
  Filter,
  Bell,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { FloatingOrbs, AmbientDust, PulseDot } from "@/components/landing/AmbientMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("sb_admin_token") || "";
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const relTime = (iso) => {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/**
 * /admin/chat-leads — recap of every Sable chat session that captured
 * contact info or was escalated to a human. Lets the admin browse, open
 * the full transcript, and delete stale leads.
 */
const AdminChatLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [escalatedCount, setEscalatedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [selected, setSelected] = useState(null);   // full detail doc
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(
    async (opts = {}) => {
      const showSpinner = !opts.silent;
      if (showSpinner) setRefreshing(true);
      try {
        const { data } = await axios.get(`${API}/admin/chat-leads`, {
          headers: authHeaders(),
          params: { limit: 100, escalated_only: escalatedOnly },
        });
        setLeads(data.leads || []);
        setTotal(data.total || 0);
        setEscalatedCount(data.escalated || 0);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/admin/login", { replace: true });
          return;
        }
        toast.error("Failed to load chat leads.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [navigate, escalatedOnly],
  );

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (sessionId) => {
    setLoadingDetail(true);
    setSelected({ session_id: sessionId, messages: [] });
    try {
      const { data } = await axios.get(`${API}/admin/chat-leads/${sessionId}`, {
        headers: authHeaders(),
      });
      setSelected(data);
    } catch {
      toast.error("Failed to load transcript.");
      setSelected(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const deleteLead = async (sessionId) => {
    if (!window.confirm("Delete this chat lead and its full transcript? This can't be undone.")) return;
    setDeletingId(sessionId);
    try {
      await axios.delete(`${API}/admin/chat-leads/${sessionId}`, {
        headers: authHeaders(),
      });
      toast.success("Chat lead deleted.");
      setLeads((prev) => prev.filter((l) => l.session_id !== sessionId));
      setTotal((n) => Math.max(0, n - 1));
      if (selected?.session_id === sessionId) setSelected(null);
    } catch {
      toast.error("Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden" data-testid="admin-chat-leads-page">
      <FloatingOrbs colors={["#22c55e", "#00f0ff", "#a78bfa"]} />
      <AmbientDust count={16} color="#22c55e" />
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[6%] right-[10%] w-[520px] h-[520px] rounded-full bg-[#22c55e]/12 blur-[160px]" />
        <div className="absolute bottom-[-15%] left-[-5%] w-[420px] h-[420px] rounded-full bg-[#a78bfa]/12 blur-[140px]" />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-5 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <Link
              to="/admin"
              data-testid="chat-leads-back"
              className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white transition mb-3"
            >
              <ArrowLeft size={13} /> Back to hub
            </Link>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#22c55e] mb-3">
              <PulseDot color="#22c55e" size={7} />
              Sable · chat inbox
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Chat <span className="text-[#22c55e] text-glow-cyan">leads.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              Every visitor Sable captured. Push notifications fire the moment a lead lands or a chat is escalated.
            </p>
          </div>
          <div className="flex items-center flex-wrap gap-2 self-start md:self-auto">
            <button
              onClick={() => setEscalatedOnly((v) => !v)}
              data-testid="chat-leads-filter-escalated"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition ${
                escalatedOnly
                  ? "bg-[#ff0055]/15 border-[#ff0055]/50 text-[#ff5577]"
                  : "bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.08]"
              }`}
            >
              <Filter size={11} /> {escalatedOnly ? "Escalated only" : "Show all"}
            </button>
            <button
              onClick={() => load()}
              data-testid="chat-leads-refresh"
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs text-white/80 hover:bg-white/[0.08] transition disabled:opacity-50"
            >
              <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
          <StatCard label="Total leads" value={total} color="#22c55e" icon={MessageSquare} />
          <StatCard label="Escalated to human" value={escalatedCount} color="#ff0055" icon={Bell} />
          <StatCard label="Showing now" value={leads.length} color="#00f0ff" icon={Filter} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={26} className="animate-spin text-[#22c55e]" />
          </div>
        ) : leads.length === 0 ? (
          <EmptyState escalatedOnly={escalatedOnly} />
        ) : (
          <ul className="space-y-3">
            {leads.map((lead, i) => (
              <LeadRow
                key={lead.session_id}
                lead={lead}
                index={i}
                onOpen={() => openDetail(lead.session_id)}
                onDelete={() => deleteLead(lead.session_id)}
                deleting={deletingId === lead.session_id}
              />
            ))}
          </ul>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <TranscriptModal
            lead={selected}
            loading={loadingDetail}
            onClose={() => setSelected(null)}
            onDelete={() => deleteLead(selected.session_id)}
            deleting={deletingId === selected.session_id}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon }) => (
  <div
    className="rounded-2xl border p-4 backdrop-blur-md"
    style={{ background: `${color}0d`, borderColor: `${color}40` }}
    data-testid={`chat-leads-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center gap-2 mb-2">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center border"
        style={{ background: `${color}20`, borderColor: `${color}55`, color }}
      >
        <Icon size={14} />
      </div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">{label}</div>
    </div>
    <div className="text-3xl font-display font-black" style={{ color }}>
      {value}
    </div>
  </div>
);

const LeadRow = ({ lead, index, onOpen, onDelete, deleting }) => {
  const { contact = {} } = lead;
  const who = contact.name || contact.email || contact.phone || "Anonymous visitor";
  return (
    <motion.li
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.03, 0.4) }}
      data-testid={`chat-lead-row-${lead.session_id}`}
      className="group relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-4 md:p-5 hover:border-white/25 hover:bg-white/[0.055] transition-all"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center border flex-shrink-0"
          style={{
            background: lead.escalated ? "#ff005520" : "#22c55e20",
            borderColor: lead.escalated ? "#ff005555" : "#22c55e55",
            color: lead.escalated ? "#ff5577" : "#22c55e",
          }}
        >
          {lead.escalated ? <Bell size={18} /> : <MessageSquare size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-display font-bold text-base tracking-tight truncate">{who}</div>
            {lead.escalated && (
              <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-[#ff0055]/15 border border-[#ff0055]/40 text-[#ff5577]">
                Escalated
              </span>
            )}
            <span className="text-[9px] uppercase tracking-[0.22em] text-white/45">{relTime(lead.updated_at || lead.created_at)}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[#a395c3]">
            {contact.email && (
              <span className="inline-flex items-center gap-1.5"><Mail size={11} /> {contact.email}</span>
            )}
            {contact.phone && (
              <span className="inline-flex items-center gap-1.5"><Phone size={11} /> {contact.phone}</span>
            )}
            {contact.name && contact.email && (
              <span className="inline-flex items-center gap-1.5"><User size={11} /> {contact.name}</span>
            )}
            <span>{lead.message_count} msgs</span>
          </div>
          {lead.admin_summary && (
            <p className="mt-2 text-[13px] text-white/75 line-clamp-2 whitespace-pre-line">
              {lead.admin_summary.split("\n").slice(0, 3).join(" · ")}
            </p>
          )}
        </div>
        <div className="flex flex-col md:flex-row items-end md:items-center gap-2 flex-shrink-0">
          {contact.email && (
            <a
              href={`mailto:${contact.email}?subject=Following%20up%20on%20your%20chat%20with%20SB%20Media%20Studio&body=${encodeURIComponent(`Hey ${contact.name ? contact.name.split(' ')[0] : 'there'},\n\nThanks for reaching out through the site chat — following up on what we were talking about.\n\n`)}`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`chat-lead-reply-email-${lead.session_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/45 text-[#22c55e] text-[11px] font-semibold hover:bg-[#22c55e] hover:text-[#05050a] transition"
              title={`Email ${contact.email}`}
            >
              <Mail size={11} /> Email
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`chat-lead-reply-phone-${lead.session_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#a78bfa]/15 border border-[#a78bfa]/45 text-[#a78bfa] text-[11px] font-semibold hover:bg-[#a78bfa] hover:text-[#05050a] transition"
              title={`Call ${contact.phone}`}
            >
              <Phone size={11} /> Call
            </a>
          )}
          <button
            onClick={onOpen}
            data-testid={`chat-lead-open-${lead.session_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00f0ff]/12 border border-[#00f0ff]/45 text-[#00f0ff] text-[11px] font-semibold hover:bg-[#00f0ff] hover:text-[#05050a] transition"
          >
            <ExternalLink size={11} /> View
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            data-testid={`chat-lead-delete-${lead.session_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/15 text-white/70 hover:bg-[#ff0055]/15 hover:text-[#ff5577] hover:border-[#ff0055]/50 text-[11px] font-semibold transition disabled:opacity-50"
          >
            {deleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Delete
          </button>
        </div>
      </div>
    </motion.li>
  );
};

const TranscriptModal = ({ lead, loading, onClose, onDelete, deleting }) => {
  const { contact = {}, messages = [] } = lead;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="chat-lead-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[85vh] rounded-3xl bg-[#0b0715] border border-white/10 overflow-hidden flex flex-col"
      >
        <div className="flex items-start justify-between p-5 md:p-6 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#22c55e] mb-1">Chat transcript</div>
            <h2 className="font-display font-bold text-xl tracking-tight truncate">
              {contact.name || contact.email || contact.phone || "Anonymous"}
            </h2>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#a395c3]">
              {contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  className="inline-flex items-center gap-1.5 hover:text-white"
                >
                  <Mail size={10} /> {contact.email}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1.5 hover:text-white">
                  <Phone size={10} /> {contact.phone}
                </a>
              )}
              <span>{lead.message_count || messages.length} msgs</span>
              {lead.referrer && <span className="truncate max-w-[220px]">from {lead.referrer}</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="chat-lead-modal-close"
            className="w-8 h-8 rounded-full bg-white/[0.05] hover:bg-white/15 text-white/70 hover:text-white flex items-center justify-center transition flex-shrink-0 ml-2"
          >
            <X size={14} />
          </button>
        </div>

        {lead.admin_summary && (
          <div className="px-5 md:px-6 py-4 bg-[#22c55e]/[0.05] border-b border-[#22c55e]/20">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#22c55e] mb-1.5">AI briefing</div>
            <pre className="whitespace-pre-wrap text-[12px] text-white/85 font-sans leading-relaxed">
              {lead.admin_summary}
            </pre>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-3" data-testid="chat-lead-transcript">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={22} className="animate-spin text-[#22c55e]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-[#a395c3] italic py-8 text-center">No messages captured for this session.</div>
          ) : (
            messages.map((m, i) => (
              <div
                key={`${m.created_at || "no-ts"}-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-white"
                      : "bg-white/[0.05] border border-white/10 text-white/90"
                  }`}
                >
                  <div className="text-[9px] uppercase tracking-[0.24em] opacity-60 mb-1">
                    {m.role === "user" ? "Visitor" : "Sable"}
                  </div>
                  {m.content}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between p-4 md:p-5 border-t border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">
            Session · {lead.session_id?.slice(0, 8)}…
          </div>
          <button
            onClick={onDelete}
            disabled={deleting}
            data-testid="chat-lead-modal-delete"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.04] border border-white/15 text-white/70 hover:bg-[#ff0055] hover:text-white hover:border-[#ff0055] text-[12px] font-semibold transition disabled:opacity-50"
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete lead
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const EmptyState = ({ escalatedOnly }) => (
  <div
    className="rounded-3xl border border-dashed border-white/15 bg-white/[0.015] p-12 text-center"
    data-testid="chat-leads-empty"
  >
    <div className="w-16 h-16 mx-auto rounded-2xl bg-[#22c55e]/10 border border-[#22c55e]/30 text-[#22c55e] flex items-center justify-center mb-4">
      <MessageSquare size={24} />
    </div>
    <div className="font-display font-bold text-xl tracking-tight mb-1.5">No chat leads yet</div>
    <p className="text-sm text-[#a395c3] max-w-md mx-auto">
      {escalatedOnly
        ? "Nothing escalated to a human yet. Switch off the filter to see every captured lead."
        : "As soon as Sable captures a name, email, or phone number — or a visitor asks to talk to a human — it'll show up here and ping your devices."}
    </p>
  </div>
);

export default AdminChatLeads;
